import React, { useCallback } from 'react';
import { format, FormatOptionsWithLanguage } from 'sql-formatter';
import { FullWidthBannerColorPalette } from '../../../banner/interfaces/fullWidthBanner';
import { FormatterModes } from '../../interfaces/editor';
import { getErrorMessageFromObj } from '../../../core/utils/request';
import { getDataIdGenerator } from '../../../core/utils/generateDataId';
import { FullWidthBanner } from '../../../banner/components/full-width-banner/FullWidthBanner';
import { UserConsentError } from '../../../access-control/components/user-consent-error/UserConsentError';
import { UserRbacAccessError } from '../../../access-control/components/rbac-access-error/UserRbacAccessError';
import { HdCodeEditor } from '../code-editor';
import styles from './QueryEditor.module.scss';

require('codemirror/mode/sql/sql');

export type QueryChangeFunction = (string: string) => void;

export interface QueryEditorProps {
  dataId: string;
  wrapperClassName?: string;
  className?: string;
  value: string;
  autoHeight?: boolean;
  allowCopy?: boolean;
  mode?: string;
  error?: any;
  showUserAccessError?: boolean;
  showRbacAccessError?: boolean;
  formatOptions?: FormatOptionsWithLanguage;
  onChange?: QueryChangeFunction;
  getCodeMirror?: Function;
  focusEditorOnLoad?: boolean;
  allowFormatting?: boolean;
}

export default function QueryEditor({
  dataId,
  wrapperClassName = '',
  className = '',
  value,
  autoHeight = false,
  allowCopy = false,
  mode = 'text/x-sql',
  error,
  showUserAccessError,
  showRbacAccessError = false,
  formatOptions = {
    language: FormatterModes.SQL
  },
  onChange = () => {},
  getCodeMirror,
  focusEditorOnLoad,
  allowFormatting = true
}: QueryEditorProps) {
  const dataIdGenerator = useCallback(getDataIdGenerator(dataId), [dataId]);

  const errorMessage = getErrorMessageFromObj(error);

  return (
    <div className={`${styles.sqlEditorWrapper} ${wrapperClassName}`}>
      <HdCodeEditor
        dataId={dataIdGenerator('query-editor')}
        className={`${styles.sqlEditor} ${className}`}
        autocomplete
        showLineNumbers
        readonly={false}
        allowFormatting={allowFormatting}
        resizeCtrl
        autoHeight={autoHeight}
        allowCopy={allowCopy}
        value={value}
        onChange={onChange}
        editorOptions={{
          mode
        }}
        formatter={rawValue => format(rawValue, formatOptions)}
        copyTooltipContent='Copy Query'
        formatTooltipContent='Format Query'
        getCodeMirror={getCodeMirror}
        autofocus={focusEditorOnLoad}
      />

      <FullWidthBanner
        open={!!errorMessage}
        dataId={dataIdGenerator('error')}
        color={FullWidthBannerColorPalette.DEFERRED}
        icon='error-filled'
      >
        {errorMessage}
      </FullWidthBanner>

      {showUserAccessError ? <UserConsentError /> : null}

      {showRbacAccessError ? <UserRbacAccessError /> : null}
    </div>
  );
}
